import { useEffect, useRef } from 'react';
import { removeClip } from './doc/clipOps';
import { removeEffect } from './doc/effectOps';
import type { MusicVideoEditorContextValue } from './MusicVideoEditorContext';

/** True while the user types in a field: the editor keys stay off. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/**
 * The keys of the editor: Space plays and pauses, Cmd/Ctrl+Z undoes, Shift+Cmd/Ctrl+Z and
 * Ctrl+Y redo, Delete and Backspace remove the selected clip (the clip before fills it) or the
 * selected effect, Escape clears the selection.
 */
export function useEditorKeys(context: MusicVideoEditorContextValue | null): void {
  const live = useRef(context);
  live.current = context;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const editor = live.current;
      if (editor === null || isTyping(event.target)) {
        return;
      }
      const isMod = event.metaKey || event.ctrlKey;
      if (event.key === ' ' && !isMod) {
        event.preventDefault();
        editor.transport.toggle();
      } else if (isMod && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (!editor.readOnly) {
          editor.dispatch({ type: event.shiftKey ? 'redo' : 'undo' });
        }
      } else if (isMod && event.key.toLowerCase() === 'y') {
        event.preventDefault();
        if (!editor.readOnly) {
          editor.dispatch({ type: 'redo' });
        }
      } else if ((event.key === 'Delete' || event.key === 'Backspace') && editor.selection?.kind === 'clip') {
        event.preventDefault();
        const { id } = editor.selection;
        if (!editor.readOnly) {
          editor.change((doc) => removeClip(doc, id));
          editor.select(null);
        }
      } else if ((event.key === 'Delete' || event.key === 'Backspace') && editor.selection?.kind === 'effect') {
        event.preventDefault();
        const { id } = editor.selection;
        if (!editor.readOnly) {
          editor.change((doc) => removeEffect(doc, id));
          editor.select(null);
        }
      } else if (event.key === 'Escape') {
        editor.select(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
